import type { PermissionService } from "@verbleif/shared/src/composables/Permissions/PermissionService";
import type { NavigationGuard } from "vue-router";
import { canOpenAppStore } from "@verbleif/shared/src/composables/AppStore/useAppStoreAccess";
import { loadMyAppStoreAccess } from "@verbleif/shared/src/composables/AppStore/useMyAppStoreAccess";
import { useAuthStore } from "@verbleif/shared/src/composables/AuthStore/useAuthStore";
import { useLocationStore } from "@verbleif/shared/src/composables/LocationStore/useLocationStore";
import { permissionService } from "@verbleif/shared/src/composables/Permissions/SharedPermissions";
import { until } from "@vueuse/core";
import { computed } from "vue";

/**
 * Route guard for the App Store / Integrations page. It opens for whoever can use some part of it in the
 * selected organization: the marketplace (install read), the Developer tab (developer grants or a developer
 * profile share) or the installs the person owns or was given a share on. Everyone else (including direct URL /
 * deep-link) is redirected to a page they can always reach instead of landing on a page that only 403s, with the
 * query and hash of the link.
 *
 * On a cold reload / deep-link the user, clients, locations and permissions load asynchronously
 * (refreshToken -> onAuthenticateSuccess). We wait until they are resolvable before deciding, so a
 * permitted user is never wrongly redirected; the wait is bounded because a failed refresh redirects
 * to login on its own and must not hang navigation.
 */
export function createAppStoreBeforeEnter(service: PermissionService = permissionService): NavigationGuard {
  return async (to) => {
    const authStore = useAuthStore();
    const locationStore = useLocationStore();

    await until(
      computed(() => authStore.user.value !== null && locationStore.selectedLocationObject.value !== null),
    ).toBe(true, { timeout: 5000, throwOnTimeout: false });

    const clientIri = locationStore.selectedLocationObject.value?.client ?? null;
    await loadMyAppStoreAccess(clientIri);

    if (canOpenAppStore(clientIri, service)) {
      return true;
    }

    // Keeps the query and hash, e.g. ?notification=<IRI> of a notification link, so the item is still read there.
    return { name: "my-account", query: to.query, hash: to.hash };
  };
}

export const appStoreBeforeEnter = createAppStoreBeforeEnter();
